import React, { useMemo, useContext } from 'react';

import ComparisonCardBlock from 'components/ComparisonCardBlock';
import { useComparisonCardBlockQuery } from 'hooks/useComparisonCardBlockQuery';
import useLiveData from 'hooks/useLiveData';
import { PersonalizationContext } from 'components/Personalization/PersonalizationContext';

const ComparisonCardBlockProvider = ({ children, staticData, id }) => {
	const { matchAudience } = useContext(PersonalizationContext);
	const { data } = useComparisonCardBlockQuery();
	const shouldLimitLiveDataByStaticIds = !staticData;
	const effectiveStaticData = React.useMemo(() => staticData || data || {}, [staticData, data]);
	const comparisonCardBlockEdges = effectiveStaticData?.allComparisonCardBlock?.edges || [];
	const comparisonCardBlockNodes = comparisonCardBlockEdges.map(({ node }) => node);

	const {
		statusText: comparisonCardBlockStatusText,
		response: comparisonCardBlockResponse,
		visibilityChange: comparisonCardBlockVisibilityChange,
	} = useLiveData({
		namespace: 'SmartTags',
		model: 'ComparisonCardBlock',
		method: 'all',
		graphqlKey: 'allSmartTagsComparisonCardBlock',
		apiParams: {
			archived: false,
			approved: true,
			deleted: false,
			_join: 'Audience',
			...(shouldLimitLiveDataByStaticIds && comparisonCardBlockNodes.length > 0 ? {
				id: comparisonCardBlockNodes.map((n) => n.id).join(','),
			} : {}),
			...(!!id ? { id } : {}),
		},
		staticData: effectiveStaticData,
	});

	const liveComparisonCardBlock = comparisonCardBlockResponse?.data[0];
	let ts = comparisonCardBlockNodes[0];
	if (!!liveComparisonCardBlock && comparisonCardBlockStatusText === 'LOADED') {
		ts = liveComparisonCardBlock;
	}
	if (!!liveComparisonCardBlock && comparisonCardBlockVisibilityChange) ts = liveComparisonCardBlock;

	let comparisonCards = ts?.cards.map((card, index) => {
		return [
			{
				name: card?.name1,
				image: card?.image1,
				uri: card?.uri1,
				primary: true,
			},
			{
				name: card?.name2,
				image: card?.image2,
				uri: card?.uri2,
				primary: false,
			},
			{
				name: card?.name3,
				image: card?.image3,
				uri: card?.uri3,
				primary: false,
			},
		];
	});

	const datum = useMemo(() => ts, [ts]);

	if (!!datum && datum.hasOwnProperty('Audience') && datum?.Audience) {
		if (!matchAudience(datum?.Audience)) {
			return null;
		}
	}

	return (
		<ComparisonCardBlock {...datum} cards={comparisonCards} matchAudience={matchAudience} />
	);
};

export default ComparisonCardBlockProvider;